// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ItemFormEntry } from '@/orders/schemas/item';
import { Project, ProjectFulfillmentBySku } from '@/project/api/types';
import { computeFulfillmentNeed } from '@/project/utilities/compute-fulfillment-need';
import { SparseItem } from '@/sparse/api/types';
import { extractSparseItem } from '@/sparse/utilities/to-sparse-item';

/**
 * Derives the auto-fill manifest for a project-location destination: one row
 * per project SKU with outstanding need, quantity defaulted to that need,
 * ordered to mirror the project's "Products requested" list.
 *
 * Pure derivation of (project line items, fulfillment summary) — no form
 * state. Returns an empty array when nothing has outstanding need; callers
 * substitute a single blank row in that case.
 */
export function buildSuggestedManifest(
  project: Project,
  summary: ProjectFulfillmentBySku
): ItemFormEntry[] {
  // Item ID → SparseItem for the form's item search, and item ID → first
  // appearance index in project.items (ordered by line item sequence; phased
  // line items collapse to the earliest sequence so each SKU keeps one spot).
  const sparseItemById = new Map<string, SparseItem>();
  const lineItemPositionById = new Map<string, number>();
  project.items.forEach((lineItem, index) => {
    if (!sparseItemById.has(lineItem.item.id)) {
      sparseItemById.set(lineItem.item.id, extractSparseItem(lineItem));
    }
    if (!lineItemPositionById.has(lineItem.item.id)) {
      lineItemPositionById.set(lineItem.item.id, index);
    }
  });

  const entries: { entry: ItemFormEntry; position: number }[] = [];
  for (const row of summary.rows) {
    const item = sparseItemById.get(row.itemId);
    if (!item) continue;
    const { need } = computeFulfillmentNeed(row, 0);
    if (need <= 0) continue;
    entries.push({
      entry: { item, quantity: need },
      position: lineItemPositionById.get(row.itemId) ?? Number.MAX_SAFE_INTEGER,
    });
  }
  entries.sort((a, b) => a.position - b.position);
  return entries.map((e) => e.entry);
}
